'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useServerAuth } from '../contexts/auth-context';
import { checkPaymentStatus } from '../modules/profile/services';
import { useProfile } from '@/profile';
import { useFirebaseAuth } from '@/shared/hooks';

// Configuration for exempt paths and redirect destinations
const REDIRECT_CONFIG = {
  exemptPaths: {
    paymentUpdate: '/aggiorna-pagamento',
    onboarding: '/profilo/onboarding',
  },
  redirectPaths: {
    paymentUpdate: '/aggiorna-pagamento',
    onboardingStart: '/profilo/onboarding/start',
  },
};

interface RedirectCheckWrapperProps {
  children: React.ReactNode;
}

/**
 * Component that checks if the user needs to be redirected
 * and redirects to the appropriate page if necessary
 *
 * @param children The child components to render
 */
export function RedirectCheckWrapper({ children }: RedirectCheckWrapperProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, hydrated } = useServerAuth();
  const [isChecking, setIsChecking] = useState(true);
  const firebaseAuth = useFirebaseAuth();
  const { refreshProfile } = useProfile(firebaseAuth);

  // Use ref to track if checks have already run
  const checksRunRef = useRef(false);

  // Determine if current page is exempt from checks
  const isPaymentUpdatePage = pathname === REDIRECT_CONFIG.exemptPaths.paymentUpdate;
  const isOnboardingPage = pathname.startsWith(REDIRECT_CONFIG.exemptPaths.onboarding);
  // Only fully exempt payment update page; onboarding pages still need payment checks
  const isFullyExemptPage = isPaymentUpdatePage;

  useEffect(() => {
    if (isFullyExemptPage || !hydrated || !user || checksRunRef.current) {
      setIsChecking(false);
      return;
    }

    checksRunRef.current = true;
    let isMounted = true;

    const runChecks = async () => {
      try {
        let needsToUpdatePayment = false;
        try {
          needsToUpdatePayment = await checkPaymentStatus(user.uid);
          if (needsToUpdatePayment) {
            router.replace(REDIRECT_CONFIG.redirectPaths.paymentUpdate);
            return;
          }
        } catch (err) {
          // Error handling silently fails
        }

        if (!isMounted) {
          return;
        }

        if (isOnboardingPage) {
          setIsChecking(false);
          return;
        }

        let profileNotFound = false;
        let profileError = null;

        try {
          profileError = await refreshProfile({
            email: user.email,
            onSuccessCallback: () => {},
          });

          profileNotFound = profileError?.message?.includes('404') || false;
        } catch (err) {
          // Error handling silently fails
        }

        if (!isMounted) {
          return;
        }

        if (profileNotFound) {
          router.replace(REDIRECT_CONFIG.redirectPaths.onboardingStart);
          return;
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    };

    runChecks();

    return () => {
      isMounted = false;
    };
  }, [pathname, user, hydrated, isFullyExemptPage, router, refreshProfile]);

  // If checking and not on exempt pages, show nothing to prevent flicker
  if (isChecking && !isFullyExemptPage) {
    return null;
  }

  return <>{children}</>;
}
